import { useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useQuery } from '@tanstack/react-query';
import type { Vehicle, FilterState, VehicleClass } from '../types';
import {
  PROGRESS_STATUSES, VEHICLE_CLASSES, DEFAULT_FILTERS,
  PROGRESS_COLOURS, CLASS_COLOURS, classLabel,
} from '../types';
import { vehiclesApi } from '../api/vehicles';
import { dealershipsApi } from '../api/dealerships';
import { tagsApi } from '../api/tags';
import StatusBadge from '../components/StatusBadge';
import TagPill from '../components/TagPill';
import VehicleModal from '../components/VehicleModal';
import { vehicleImageSrc, formatMinutes } from '../utils/helpers';

// ─── Class groups (mirrors FilterPanel) ──────────────────────────────────────

const CLASS_GROUPS: { label: string; classes: VehicleClass[] }[] = [
  { label: 'Cars',   classes: ['S', 'A', 'B', 'C', 'D'] },
  { label: 'Police', classes: ['Police'] },
  { label: 'Bikes',  classes: ['Bike-S', 'Bike-A', 'Bike-B', 'Bike-C', 'Bike-D'] },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const barRef   = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const [modalVehicle, setModalVehicle] = useState<Vehicle | null>(null);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);

  const { data: vehiclesData }    = useQuery({ queryKey: ['vehicles', {}],    queryFn: () => vehiclesApi.list({}) });
  const { data: dealershipsData } = useQuery({ queryKey: ['dealerships'],      queryFn: () => dealershipsApi.list() });
  const { data: tagsData }        = useQuery({ queryKey: ['tags'],             queryFn: () => tagsApi.list() });

  const allVehicles = vehiclesData?.vehicles ?? [];
  const tags        = tagsData?.tags ?? [];

  // Filtered vehicles (for the card rows)
  const filtered = allVehicles.filter(v => {
    if (filters.search && !v.name?.toLowerCase().includes(filters.search.toLowerCase()) && !v.spawncode.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.vehicle_class.length && !filters.vehicle_class.includes(v.vehicle_class)) return false;
    if (filters.progress.length && !filters.progress.includes(v.progress)) return false;
    return true;
  });

  const total  = allVehicles.length;
  const merged = allVehicles.filter(v => v.progress === 'Merged into Live').length;
  const pct    = total > 0 ? Math.round((merged / total) * 100) : 0;

  // GSAP: progress bar + count-up
  useGSAP(() => {
    if (!barRef.current) return;
    gsap.fromTo(barRef.current, { width: '0%' }, { width: `${pct}%`, duration: 1.5, ease: 'power3.out', delay: 0.3 });
  }, [pct]);

  useGSAP(() => {
    if (!statsRef.current) return;
    statsRef.current.querySelectorAll('[data-count]').forEach(el => {
      const target = parseInt(el.getAttribute('data-count') ?? '0');
      const proxy  = { val: 0 };
      gsap.to(proxy, {
        val: target,
        duration: 1.2,
        ease: 'power2.out',
        delay: 0.4,
        onUpdate: () => { el.textContent = String(Math.round(proxy.val)); },
      });
    });
  }, [allVehicles.length]);

  const classCount = (c: VehicleClass) => allVehicles.filter(v => v.vehicle_class === c).length;
  const totalHandlingTime = allVehicles.reduce((s, v) => s + (v.total_handling_time ?? 0), 0);

  // Toggle helpers for pill filters
  const toggleClass = (c: string) =>
    setFilters(f => ({ ...f, vehicle_class: f.vehicle_class.includes(c) ? f.vehicle_class.filter(v => v !== c) : [...f.vehicle_class, c] }));
  const toggleProgress = (p: string) =>
    setFilters(f => ({ ...f, progress: f.progress.includes(p) ? f.progress.filter(v => v !== p) : [...f.progress, p] }));

  return (
    <div className="min-h-screen sakura-bg">
      <div className="max-w-screen-2xl mx-auto px-4 py-6 space-y-4">

        {/* ── Overall progress bar ──────────────────────────────── */}
        <div className="border px-6 py-5" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-2xl tracking-wide uppercase" style={{ color: 'var(--text)' }}>Overall Progress</h2>
            <span className="font-heading text-3xl" style={{ color: 'var(--text)' }}>{pct}%</span>
          </div>
          <div className="h-2 overflow-hidden" style={{ background: 'var(--surface)' }}>
            <div ref={barRef} className="h-full bg-primary" style={{ width: 0 }} />
          </div>
          <p className="font-body text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
            <span className="font-heading" style={{ color: 'var(--text)' }}>{merged}</span> / {total} Merged into Live
          </p>
        </div>

        {/* ── Status stat boxes — 6 cols, end-to-end ───────────── */}
        <div ref={statsRef} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {PROGRESS_STATUSES.map(s => {
            const col = PROGRESS_COLOURS[s];
            return (
              <div key={s} className="border overflow-hidden text-center" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                {/* Coloured top accent */}
                <div style={{ height: 4, background: col.bg }} />
                <div className="px-3 py-4">
                  <p className="font-heading text-3xl" style={{ color: 'var(--text)' }} data-count={allVehicles.filter(v => v.progress === s).length}>0</p>
                  <div className="mt-2 flex justify-center">
                    <StatusBadge status={s} small />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── By Class + Handling time ──────────────────────────── */}
        <div className="grid grid-cols-3 gap-3">

          {/* By Class — 3 sub-group columns matching FilterPanel */}
          <div className="col-span-2 border px-5 py-4" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <h3 className="font-heading text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--text-muted)' }}>By Class</h3>
            <div className="grid grid-cols-3 gap-x-6">
              {CLASS_GROUPS.map(group => (
                <div key={group.label}>
                  {/* Group header */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-heading text-[9px] uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>{group.label}</span>
                    <div className="flex-1 border-t" style={{ borderColor: 'var(--border)' }} />
                  </div>
                  {/* Class rows */}
                  <div className="flex flex-col gap-1">
                    {group.classes.map(vc => {
                      const col   = CLASS_COLOURS[vc];
                      const count = classCount(vc);
                      return (
                        <div key={vc} className="flex items-center gap-2 py-1" style={{ borderLeft: `3px solid ${col.bg}`, paddingLeft: 8 }}>
                          {/* Colour swatch */}
                          <span style={{ width: 8, height: 8, flexShrink: 0, background: col.bg, display: 'inline-block' }} />
                          <span className="font-heading text-xs flex-1" style={{ color: 'var(--text)' }}>{classLabel(vc)}</span>
                          <span className="font-body text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Handling time */}
          <div className="border flex flex-col items-center justify-center px-5 py-6 gap-2" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <p className="font-heading text-5xl" style={{ color: 'var(--text)' }}>{formatMinutes(totalHandlingTime)}</p>
            <p className="font-body text-sm" style={{ color: 'var(--text-muted)' }}>Total Handling Time Logged</p>
          </div>
        </div>

        {/* ── Filters — pill-based class + status ──────────────── */}
        <div className="border px-6 py-5 space-y-5" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>

          {/* Search row — centered */}
          <div className="flex items-center justify-center gap-3">
            <input
              className="input w-72"
              placeholder="Search vehicles…"
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            />
            <span className="font-body text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
              {filtered.length} / {total} vehicles
            </span>
            {(filters.search || filters.vehicle_class.length > 0 || filters.progress.length > 0) && (
              <button
                onClick={() => setFilters(DEFAULT_FILTERS)}
                className="text-xs font-heading uppercase tracking-wide hover:text-primary transition-colors"
                style={{ color: 'var(--text-muted)' }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Class pills — centered, grouped */}
          <div className="text-center">
            <p className="font-heading text-[9px] uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Class</p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {CLASS_GROUPS.map((group, gi) => (
                <div key={group.label} className="flex items-center gap-1.5">
                  {gi > 0 && <div className="h-5 w-px mx-1" style={{ background: 'var(--border)' }} />}
                  {group.classes.map(vc => {
                    const col    = CLASS_COLOURS[vc];
                    const active = filters.vehicle_class.includes(vc);
                    return (
                      <button
                        key={vc}
                        type="button"
                        onClick={() => toggleClass(vc)}
                        className="font-heading text-[11px] uppercase tracking-wide transition-all"
                        style={{
                          padding: '4px 12px',
                          border: `1px solid ${active ? col.bg : 'var(--border-strong)'}`,
                          background: active ? col.bg : 'transparent',
                          color: active ? col.text : 'var(--text-muted)',
                          cursor: 'pointer',
                        }}
                      >
                        {classLabel(vc)}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Progress status pills — centered */}
          <div className="text-center">
            <p className="font-heading text-[9px] uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Progress</p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {PROGRESS_STATUSES.map(s => {
                const col    = PROGRESS_COLOURS[s];
                const active = filters.progress.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleProgress(s)}
                    className="font-heading text-[11px] uppercase tracking-wide transition-all"
                    style={{
                      padding: '4px 12px',
                      border: `1px solid ${active ? col.bg : 'rgba(0,0,0,0.15)'}`,
                      background: active ? col.bg : 'transparent',
                      color: active ? col.text : '#6B6B6B',
                      cursor: 'pointer',
                    }}
                  >
                    {col.label}
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* ── Vehicle grid (read-only) ──────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {filtered
            .sort((a, b) => (a.name ?? a.spawncode).localeCompare(b.name ?? b.spawncode))
            .map(v => (
              <div
                key={v.id}
                className="overflow-hidden cursor-pointer transition-all hover:shadow-md"
                onClick={() => setModalVehicle(v)}
                style={{
                  background:   'var(--card)',
                  borderTop:    `3px solid ${CLASS_COLOURS[v.vehicle_class]?.bg ?? '#374151'}`,
                  borderRight:  '1px solid var(--border)',
                  borderBottom: '1px solid var(--border)',
                  borderLeft:   '1px solid var(--border)',
                }}
              >
                <div className="h-24 flex items-center justify-center overflow-hidden" style={{ background: 'var(--surface)' }}>
                  {vehicleImageSrc(v) ? (
                    <img src={vehicleImageSrc(v)!} alt={v.name ?? v.spawncode} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span className="text-[10px] font-body" style={{ color: 'var(--text-muted)' }}>No image</span>
                  )}
                </div>
                <div className="px-2 py-2 flex flex-col items-center text-center gap-1">
                  <p className="font-heading text-xs leading-tight line-clamp-2 w-full" style={{ color: 'var(--text)' }}>{v.name ?? v.spawncode}</p>
                  <p className="text-[10px] font-body" style={{ color: 'var(--text-muted)' }}>{v.vehicle_class}</p>
                  <StatusBadge status={v.progress} small />
                  {v.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-0.5 justify-center">
                      {v.tags.slice(0, 2).map(t => <TagPill key={t.id} tag={t} small />)}
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>

      </div>

      {modalVehicle && (
        <VehicleModal
          vehicle={modalVehicle}
          onClose={() => setModalVehicle(null)}
          tags={tags}
          dealerships={dealershipsData?.dealerships ?? []}
        />
      )}
    </div>
  );
}
